import { useEffect, useState } from 'react';
import type { MessageDeliveryDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { FilterBar, FilterField, ToggleGroup } from '@/components/filters';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { dateTime } from '@/lib/format';
import { MailIcon } from '@/components/icons';
import { ListCard, ListToolbar, TableScroll, Table, Td, Th, Tr } from '@/components/page';
import { Alert, Button, EmptyState, Pill, TableSkeleton } from '@/components/ui';
import { SettingsPage } from '@/pages/settings';

/**
 * Every email and text this store sent, or tried to (ADR-034).
 *
 * Built for one question — *did the customer get it?* — so failures are one
 * click away, carry the provider's own reason, and can be sent again once
 * whatever broke is fixed.
 */
export function MessagesPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);

  const [rows, setRows] = useState<MessageDeliveryDto[]>([]);
  const [failures, setFailures] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const load = async (next?: { channel?: string; status?: string; search?: string }) => {
    const query = {
      channel: next?.channel ?? channel,
      status: next?.status ?? status,
      search: next?.search ?? search,
    };

    if (next?.channel !== undefined) setChannel(next.channel);
    if (next?.status !== undefined) setStatus(next.status);
    if (next?.search !== undefined) setSearch(next.search);

    try {
      const [page, count] = await Promise.all([
        withSession(() =>
          adminApi.messages.list({
            limit: 50,
            ...(query.channel ? { channel: query.channel as 'EMAIL' | 'SMS' | 'WHATSAPP' } : {}),
            ...(query.status ? { status: query.status as 'SENT' | 'FAILED' } : {}),
            ...(query.search.trim() ? { search: query.search.trim() } : {}),
          }),
        ),
        withSession(() => adminApi.messages.failures()),
      ]);

      setRows(page.items);
      setFailures(count.count);
    } catch (cause) {
      setError(errorMessage(cause, 'Could not load the message log.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <SettingsPage title="messageLog" subtitle="settingsMessageLogHint" fill>
      {failures > 0 ? (
        /* The number that matters, before anyone reads a table. */
        <Alert>
          {t('messageFailuresToday')}: <strong>{failures}</strong>{' '}
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={() => void load({ status: 'FAILED' })}
          >
            {t('showThem')}
          </button>
        </Alert>
      ) : null}

      {notice ? <Alert tone="success">{notice}</Alert> : null}

      <ListCard>
        <ListToolbar>
          <FilterBar
            search={search}
            onSearch={(next) => void load({ search: next })}
            placeholder={t('recipientSearchHint')}
            shown={rows.length}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <FilterField label={t('channel')}>
                {/* Single-choice: a message goes by one channel, never two. */}
                <ToggleGroup
                  options={[
                    { value: 'EMAIL', label: t('email') },
                    { value: 'SMS', label: t('sms') },
                    { value: 'WHATSAPP', label: t('whatsapp') },
                  ]}
                  selected={channel ? [channel] : []}
                  onChange={(next) => void load({ channel: next.at(-1) ?? '' })}
                />
              </FilterField>

              <FilterField label={t('result')}>
                <ToggleGroup
                  options={[
                    { value: 'SENT', label: t('sent') },
                    { value: 'FAILED', label: t('aiFailed') },
                  ]}
                  selected={status ? [status] : []}
                  onChange={(next) => void load({ status: next.at(-1) ?? '' })}
                />
              </FilterField>
            </div>
          </FilterBar>
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading ? (
          <TableSkeleton columns={6} />
        ) : rows.length === 0 ? (
          <EmptyState title={t('noMessages')} body={t('noMessagesHint')} icon={<MailIcon />} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('when')}</Th>
                  <Th>{t('channel')}</Th>
                  <Th>{t('recipient')}</Th>
                  <Th>{t('aiTask')}</Th>
                  <Th>{t('result')}</Th>
                  <Th align="end" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Tr key={row.id}>
                    <Td className="text-xs whitespace-nowrap text-ink-muted">{dateTime(row.createdAt, locale)}</Td>
                    <Td className="text-xs text-ink-muted">
                      {row.channel === 'EMAIL' ? t('email') : row.channel === 'WHATSAPP' ? t('whatsapp') : t('sms')}
                      {/* A console driver is not a delivery, and a log that does
                          not say so is a log that lies to a store in testing. */}
                      <span className="ms-1 text-ink-faint">· {row.driver}</span>
                    </Td>
                    <Td>
                      <span dir="ltr" className="block truncate text-ink">
                        {row.to}
                      </span>
                      {row.recipientName ? <span className="text-xs text-ink-muted">{row.recipientName}</span> : null}
                    </Td>
                    <Td>
                      <span dir="ltr" className="font-mono text-xs text-ink-muted">
                        {row.event}
                      </span>
                      {row.subject ? (
                        <span className="block max-w-xs truncate text-xs text-ink-faint">{row.subject}</span>
                      ) : null}
                    </Td>
                    <Td>
                      {row.status === 'SENT' ? (
                        <span className="flex flex-wrap gap-1">
                          <Pill tone="success">{t('sent')}</Pill>
                          {/* What Meta told us after (ADR-105): reached the phone, then read. */}
                          {row.readAt ? <Pill tone="info">{t('whatsappRead')}</Pill> : row.deliveredAt ? <Pill tone="neutral">{t('whatsappDelivered')}</Pill> : null}
                        </span>
                      ) : (
                        <>
                          <Pill tone="danger">{t('aiFailed')}</Pill>
                          {row.attempt > 1 ? (
                            <span className="ms-2 text-xs text-ink-faint">
                              {t('attempt')} {row.attempt}
                            </span>
                          ) : null}
                          {row.error ? (
                            <span className="mt-1 block max-w-md text-xs text-ink-muted">{row.error}</span>
                          ) : null}
                        </>
                      )}
                    </Td>
                    <Td align="end">
                      {row.canRetry ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={busy !== null}
                          onClick={async () => {
                            setBusy(row.id);
                            setError(null);
                            setNotice(null);

                            try {
                              const result = await withSession(() => adminApi.messages.retry(row.id));
                              setNotice(result.sent ? t('resendWorked') : t('resendFailedAgain'));
                              await load();
                            } catch (cause) {
                              setError(errorMessage(cause, 'Could not send it again.'));
                            } finally {
                              setBusy(null);
                            }
                          }}
                        >
                          {busy === row.id ? t('saving') : t('sendAgain')}
                        </Button>
                      ) : null}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </ListCard>
    </SettingsPage>
  );
}
